/**
 * Main-thread (offscreen document / web page) client for
 * `opfsReceiverWorker.ts` — implements `receiveHost.ts`'s `ReceiverOutput`
 * seam over a real dedicated Worker + OPFS sync access handle. The ONLY file
 * besides the worker itself allowed to talk `postMessage` to it.
 *
 * One client = one worker = one transfer's output file (`workerProtocol.ts`'s
 * module doc). `createReceiverWorkerClient` spawns the worker; `open()`
 * sends the worker its `open` request. The worker is terminated once
 * `finalize()`/`deleteCopy()` has no further use for it.
 *
 * `writeAt` does NOT transfer its buffer to the worker (no `postMessage`
 * transfer list) — `receiveLoop.ts` keeps the SAME plaintext `Uint8Array`
 * reference around after the write resolves (to feed the running hash only
 * once a whole part authenticates, see that module's doc); transferring it
 * here would neuter the caller's reference out from under it. The
 * structured-clone copy cost is one 64 KiB buffer per chunk — cheap.
 *
 * `createWorker` is INJECTED (TASK-27 M4 extraction) rather than this module
 * constructing the `Worker` itself: the ONLY platform-specific bit of the
 * whole worker-client pipeline is how a caller resolves the worker script's
 * URL — the extension needs `browser.runtime.getURL("opfsReceiverWorker.js")`
 * (an MV3 extension-origin URL for a built asset), while a plain web page
 * uses `new URL("...", import.meta.url)`/Vite's inline-worker resolution.
 * Injecting the one-line `new Worker(...)` construction keeps this module
 * (and the postMessage protocol it drives) platform-clean, matching every
 * other pure core in this package (deps-injection over platform branching).
 */

import { type OpfsStorageDeps, requireOpfsStorage } from "./opfsSeam";
import type { ReceiverOutput } from "./receiveHost";
import type { WorkerRequest, WorkerResponse } from "./workerProtocol";

interface PendingRequest {
  resolve(res: WorkerResponse): void;
  reject(err: Error): void;
}

/**
 * Plain `Omit<WorkerRequest, "id">` is NOT distributive over the union (it
 * collapses to the properties common to every `op` variant, i.e. just
 * `{ op }`) — this variant distributes `Omit` over each member first, so
 * `fileName`/`offset`/`data`/`size`/`start`/`end` survive per-op.
 */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export interface ReceiverWorkerClient extends ReceiverOutput {
  /** Terminate the underlying worker — call once the client has no further use (after finalize/deleteCopy). */
  terminate(): void;
}

let nextRequestId = 1;

/**
 * Build a `ReceiverOutput` for `transferId`, backed by a fresh dedicated
 * worker. `open()` must be called before any other method.
 *
 * @param createWorker Platform-specific worker factory — e.g. the extension's
 *   `() => new Worker(browser.runtime.getURL("opfsReceiverWorker.js"), { type: "module" })`
 *   or the web app's `() => new Worker(new URL("...", import.meta.url), { type: "module" })`.
 */
/**
 * Platform pieces the receiver client needs.
 *
 * `createWorker` was already injected; `storage` completes the seam the module's own
 * docblock claimed (spec R2-b) — it previously reached `navigator.storage` directly.
 */
export interface ReceiverWorkerClientDeps extends OpfsStorageDeps {
  /** Spawns the OPFS receiver worker. Host-specific: bundler URL vs extension path. */
  createWorker: () => Worker;
}

export function createReceiverWorkerClient(
  transferId: string,
  deps: ReceiverWorkerClientDeps
): ReceiverWorkerClient {
  // Storage is resolved AT THE POINT OF USE, not here. `size()`, `open()` and the
  // worker error paths never touch OPFS, so demanding it at construction would make a
  // platform without OPFS unable to build a client it could legitimately use — and
  // would fail tests that exercise only the worker protocol. Spec R2-d is scoped to
  // entry points "needing OPFS"; these two are the ones that do.
  const worker = deps.createWorker();
  const pending = new Map<number, PendingRequest>();
  const fileName = `recv-${transferId}`;
  // Guards `release()`/`terminate()` re-entrancy (MUST-2 review fix, docs/file-transfer-p2p.md
  // §16 pt.4): once the worker has been terminated, a `postMessage`-based `close` request would
  // never get a response (the worker is dead), hanging forever — this flag makes BOTH methods
  // safe to call more than once, and safe together in either order.
  let released = false;
  function markReleased(): void {
    released = true;
  }

  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const res = event.data;
    const entry = pending.get(res.id);
    if (!entry) return; // stale/unknown response id — ignore
    pending.delete(res.id);
    entry.resolve(res);
  };
  worker.onerror = (event: ErrorEvent) => {
    // Reject every in-flight request — a worker-level error (e.g. an
    // uncaught exception before postMessage) leaves them unresolved otherwise.
    const err = new Error(event.message || "opfsReceiverWorker error");
    for (const [id, entry] of pending) {
      pending.delete(id);
      entry.reject(err);
    }
  };

  function send(req: WorkerRequest): Promise<WorkerResponse> {
    return new Promise((resolve, reject) => {
      pending.set(req.id, { resolve, reject });
      worker.postMessage(req);
    });
  }

  async function request(
    reqWithoutId: DistributiveOmit<WorkerRequest, "id">
  ): Promise<Extract<WorkerResponse, { ok: true }>> {
    const id = nextRequestId++;
    const req = { id, ...reqWithoutId } as WorkerRequest;
    const res = await send(req);
    if (!res.ok) {
      const e = new Error(`opfsReceiverWorker: ${res.error}`);
      // Reconstruct the original error's name across the postMessage boundary (a
      // DOMException can't be structured-cloned) so callers can classify it — e.g.
      // receiveHost/receiveResume's STORAGE_FULL check needs the "QuotaExceededError"
      // name, which is otherwise lost behind the generic "Error". Message is
      // unchanged, so the getSize "no open access handle" substring check still works.
      if (res.errorName) e.name = res.errorName;
      throw e;
    }
    return res;
  }

  return {
    async open() {
      await request({ op: "open", fileName });
    },
    async writeAt(offset, data) {
      await request({ op: "writeAt", offset, data });
    },
    async truncate(size) {
      await request({ op: "truncate", size });
    },
    async readRange(start, end) {
      const res = await request({ op: "readRange", start, end });
      if (res.op !== "readRange") {
        throw new Error("opfsReceiverWorker: unexpected response op for readRange");
      }
      return res.data;
    },
    async flush() {
      await request({ op: "flush" });
    },
    async getSize() {
      try {
        const res = await request({ op: "getSize" });
        if (res.op !== "getSize") {
          throw new Error("opfsReceiverWorker: unexpected response op for getSize");
        }
        return res.size;
      } catch (err) {
        // `requireHandle()` in opfsReceiverWorker.ts's `getSize` case is the
        // ONLY thing that can fail there, and only when `open()` hasn't been
        // called yet on THIS worker instance — the legitimate "output
        // doesn't exist yet" case (mirrors `SenderSource.getSize`'s null
        // contract, receiveHost.ts's `ReceiverOutput.getSize` doc). Any OTHER
        // failure — a worker-level error via `worker.onerror`, or a
        // genuinely unexpected response shape — is a real problem; surface
        // it rather than silently reporting "doesn't exist". No richer error
        // TAXONOMY crosses the worker's postMessage boundary (every op's
        // `ok:false` carries only a formatted string, see workerProtocol.ts),
        // so a message-substring check is this module's only option here.
        if (err instanceof Error && err.message.includes("no open access handle")) {
          return null;
        }
        throw err;
      }
    },
    async finalize() {
      // Release the sync access handle's exclusive lock, THEN read the file
      // back via the main thread's own (async) OPFS API — see the worker's
      // module doc for why this avoids a postMessage File hand-off.
      await request({ op: "close" });
      const root = await requireOpfsStorage(deps.storage, "finalize()").getDirectory();
      const dir = await root.getDirectoryHandle("ft", { create: true });
      const handle = await dir.getFileHandle(fileName, { create: false });
      return handle.getFile();
    },
    async deleteCopy() {
      // Best-effort close first — removeEntry fails while a sync access
      // handle still holds the file open (failure-path cleanup may run
      // before any close has happened yet).
      await request({ op: "close" }).catch(() => {});
      const root = await requireOpfsStorage(deps.storage, "deleteCopy()")
        .getDirectory()
        .catch(() => null);
      if (!root) return;
      const dir = await root.getDirectoryHandle("ft", { create: false }).catch(() => null);
      if (!dir) return;
      await dir.removeEntry(fileName).catch(() => {});
    },
    async release() {
      // MUST-2 review fix (docs/file-transfer-p2p.md §16 pt.4): release the exclusive
      // sync-access-handle lock (best-effort `close`) AND terminate this worker BEFORE the P2P
      // demotion handoff spins up a brand-new worker on the SAME OPFS file — otherwise the new
      // worker's `open()`/`createSyncAccessHandle()` deadlocks against this one's still-held
      // lock. `close` is awaited (not fire-and-forget) so the handle is provably released — its
      // `FileSystemSyncAccessHandle.close()` call runs synchronously inside the worker's single
      // event loop before it posts the response back — before this promise resolves.
      if (released) return;
      markReleased();
      await request({ op: "close" }).catch(() => {});
      worker.terminate();
    },
    terminate() {
      // Idempotent alongside `release()` above (guards the same `released` flag) — this is the
      // pre-existing dispose() path (offscreen/index.ts's finally), which still runs AFTER a P2P
      // demotion's early `release()`; without the guard it would be harmless anyway (`Worker
      // .terminate()` is safely re-entrant), but sharing the flag keeps the intent explicit.
      if (released) return;
      markReleased();
      worker.terminate();
    },
  };
}
